// Web push (FCM) opt-in. Notifications only reach a player when their tab is
// closed/backgrounded — the foreground handler stays silent so someone looking
// at the game never gets an OS pop-up. Entirely opt-in via a Settings toggle;
// nothing registers until the user turns it on and grants permission.
import { getMessaging, getToken, isSupported, onMessage } from 'firebase/messaging';
import { app } from './firebase';
import { registerPush } from './game/api';

// PUBLIC VAPID key from the Firebase console (Web Push certificates). Ships to
// browsers by design — not a secret, like the rest of firebaseConfig.
const VAPID_KEY =
  'BPV5-YCENJPyS36BQlw6JT-B7m628ZnkfZoke69f-VFIPHDvtUP9LTQIAoKXRbWLkfiPMEMSrG5u7PaAx7N0P1o';

const KEY = 'neonfall.push';

export function pushEnabled(): boolean {
  return localStorage.getItem(KEY) === 'on';
}

/** Push needs Notifications + a service worker + a supported FCM environment. */
export async function isPushSupported(): Promise<boolean> {
  try {
    return (
      typeof Notification !== 'undefined' &&
      'serviceWorker' in navigator &&
      (await isSupported())
    );
  } catch {
    return false;
  }
}

/**
 * Ask for permission, register the SW, fetch the FCM token and hand it to the
 * server. Returns true only if the whole chain succeeds and notifications are
 * now on. Any failure (unsupported, denied, blocked) resolves false and leaves
 * push off.
 */
export async function enablePush(): Promise<boolean> {
  if (!(await isPushSupported())) return false;
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return false;
  try {
    const reg = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    const messaging = getMessaging(app);
    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: reg,
    });
    if (!token) return false;
    await registerPush(token);
    // Foreground messages: swallow them (no OS pop-up while you're looking).
    onMessage(messaging, () => {});
    localStorage.setItem(KEY, 'on');
    return true;
  } catch {
    return false;
  }
}

export function disablePush(): void {
  // The token stays server-side (harmless — sends just stop mattering once the
  // browser permission is revoked); we only forget the local opt-in flag.
  localStorage.setItem(KEY, 'off');
}
